import { LocationType } from "@/types/filters";
import { storeLocationDetails } from "@/utils/location-details-store";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface Location {
	id: string;
	name: string;
	type: LocationType;
	coordinates?: {
		latitude: number;
		longitude: number;
	};
}

export interface LocationsState {
	locations: Location[];
	setLocations: (locations: Location[]) => void;
	addLocation: (location: Location) => void;
	removeLocation: (id: string) => void;
	clearLocations: () => void;
}

export function createLocationsStore({ initialLocations = [] }: { initialLocations?: Location[] } = {}) {
	return create<LocationsState>()(
		devtools(
			(set, _get) => ({
				locations: initialLocations,
				setLocations: (locations: Location[]) => {
					// Deduplicate locations before setting
					const uniqueLocations = Array.from(new Map(locations.map(loc => [loc.name.toLowerCase(), loc])).values());

					// Store location details for each location
					uniqueLocations.forEach(location => {
						if (location.coordinates || location.id) {
							storeLocationDetails(location);
						}
					});

					set({ locations: uniqueLocations });
				},
				addLocation: (location: Location) => {
					set(state => {
						// Check if location already exists (by id or by name, case-insensitive)
						const existingLocation = state.locations.find(
							loc => loc.id === location.id || loc.name.toLowerCase() === location.name.toLowerCase(),
						);

						// If location already exists, don't add it
						if (existingLocation) {
							return state; // Return current state unchanged
						}

						// Store location details before adding
						if (location.coordinates || location.id) {
							storeLocationDetails(location);
						}

						// Add the new location
						return {
							locations: [...state.locations, location],
						};
					});
				},
				removeLocation: (id: string) => {
					set(state => ({
						locations: state.locations.filter(loc => loc.id !== id),
					}));
				},
				clearLocations: () => {
					set({ locations: [] });
				},
			}),
			{ name: "locations-store" },
		),
	);
}
